import { useState, useEffect } from 'react'
import { MessageCircle } from 'lucide-react'

/**
 * Appearance Tab - UI Style Toggle
 * Allows switching between Standard and Glass layouts
 */
export default function AppearanceTab({ profile, onUpdateProfile, onOpenDMWithUser }) {
  const [uiStyle, setUiStyle] = useState('standard')

  // Load saved UI style on mount
  useEffect(() => {
    const savedStyle = localStorage.getItem('gjyl-ui-style')
    if (savedStyle === 'glass' || savedStyle === 'jarvis') {
      setUiStyle('jarvis') // migrate legacy glass value
    } else if (savedStyle === 'standard') {
      setUiStyle('standard')
    }
  }, [])

  const handleStyleChange = (style) => {
    setUiStyle(style)
    localStorage.setItem('gjyl-ui-style', style)
    window.dispatchEvent(new CustomEvent('ui-style-change', { detail: style }))
  }

  return (
    <div className="p-6 max-w-4xl">
      <h3 className="text-xl font-semibold text-white mb-6">Appearance</h3>

      {/* UI Style Toggle */}
      <div className="mb-6 p-4 bg-[#0a1628] rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-medium text-white">UI Style</div>
            <div className="text-sm text-[#64748b]">Switch between Standard and Jarvis layouts</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleStyleChange('standard')}
            className={`p-4 rounded-lg border-2 transition-all ${
              uiStyle === 'standard'
                ? 'border-[#00ad9f] bg-[#00ad9f]/10 text-white'
                : 'border-[#1e3a5f] bg-[#0a1628] text-[#64748b] hover:border-[#2d4a6f] hover:text-white'
            }`}
          >
            <div className="text-sm font-medium mb-1">Standard</div>
            <div className="text-xs opacity-70">Traditional sidebar layout</div>
          </button>
          <button
            onClick={() => handleStyleChange('jarvis')}
            className={`p-4 rounded-lg border-2 transition-all ${
              uiStyle === 'jarvis'
                ? 'border-cyan-400 bg-cyan-400/10 text-cyan-200'
                : 'border-[#1e3a5f] bg-[#0a1628] text-[#64748b] hover:border-[#2d4a6f] hover:text-white'
            }`}
          >
            <div className="text-sm font-medium mb-1">Jarvis</div>
            <div className="text-xs opacity-70">Floating HUD with neon accents</div>
          </button>
        </div>
        <p className="text-xs text-[#64748b] mt-3">
          Changes apply immediately. Jarvis mode features the holographic HUD layout.
        </p>
      </div>

      {/* UI Playground Section */}
      <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1e3a5f]">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🧪</div>
          <div className="flex-1">
            <h4 className="text-base font-medium text-white mb-2">UI Playground</h4>
            <p className="text-sm text-[#94a3b8] mb-3">
              This is where I design and test new layouts. Things change frequently here as I experiment with different ideas and features.
            </p>
            <p className="text-sm text-[#64748b] mb-4">
              If you see something you like (or don't like), let me know! Your feedback helps shape the app.
            </p>
            <button
              onClick={() => onOpenDMWithUser?.('PaulyBear')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00ad9f]/20 hover:bg-[#00ad9f]/30 text-[#00ad9f] rounded-lg text-sm font-medium transition-colors border border-[#00ad9f]/30"
            >
              <MessageCircle className="w-4 h-4" />
              Message PaulyBear
            </button>
            <p className="text-xs text-[#475569] mt-2">
              Search for "PaulyBear" in friends to send feedback
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
